// R17 - ONE place for the product-scope rules the Reports screen, its drilldowns, charts, exports and AI summary all follow.
//
// A product chosen on the Reports page is either a FAMILY ROOT (it has children: Skill Migration -> SM_INHOUSE, SM_MARA) or a single product. For a root the user picks the SCOPE explicitly:
//   family  = the root and every child product (the default - "Skill Migration" means the whole Skill business)
//   exact   = the root code only (children excluded) - an explicit, labelled narrowing
// A non-root product has no wider family, so its scope is always exact. The scope is sent to every request as `product_scope` so nothing can silently fall back to a different one.

export const isFamilyRoot = (products, code) => {
  if (!code || code === "all") return false;
  const p = (products || []).find((x) => x.code === code);
  return !!p && !p.parent_code && (products || []).some((c) => c.parent_code === code);
};

export const effectiveProductScope = (products, product, scope) => {
  if (!product || product === "all") return null;
  return isFamilyRoot(products, product) ? (scope === "exact" ? "exact" : "family") : "exact";
};

// "product_code=X&product_scope=family" (or "" for all products) - appended to every report request.
export const productQuery = (products, product, scope) => {
  if (!product || product === "all") return "";
  return `product_code=${encodeURIComponent(product)}&product_scope=${effectiveProductScope(products, product, scope)}`;
};

// A plain-language line for the effective-scope banner, from the `scope` block every report response carries.
const hm = (iso) => (iso ? `${String(iso).slice(0, 10)} ${String(iso).slice(11, 16)}` : "");

export const describeScope = (scope) => {
  if (!scope) return null;
  const pr = scope.product || {};
  const dt = scope.date || {};
  const first = dt.first_day_ist, last = dt.last_day_included_ist;
  const rolling = String(dt.kind || "").startsWith("rolling");
  let dates = "";
  if (rolling && dt.from_ist && dt.to_ist_exclusive) {
    // a rolling "last N days" window starts at the current time of day: the first day is PART of a day, never presented as a whole one
    dates = `${hm(dt.from_ist)} to ${hm(dt.to_ist_exclusive)} IST (rolling ${String(dt.kind).replace("rolling_", "").replace("_days", "")} days - the first day is partial, the end is now)`;
  } else {
    const range = first && last ? (first === last ? `${first}` : `${first} to ${last}`) : (dt.label || "");
    dates = range ? `${range} (IST, both days included)` : "";
  }
  return {
    product: pr.label || "All products",
    mode: pr.mode || "all",
    note: pr.note || null,
    dates,
    test: scope.include_test ? "TEST records included" : "TEST records excluded",
    asOf: scope.data_as_of || null,
  };
};

// A saved AI summary describes the data AS IT WAS when it was written. The cards above are current; a summary that no longer matches them must never lead the page and must say so BEFORE its text.
// Pure, so it is tested without a browser. `rolling` = the date filter is "last N days" (the window moves every day); `nowMs` is injectable.
export const summaryStaleness = (summary, { rolling = true, nowMs = Date.now(), maxAgeHours = 24 } = {}) => {
  if (!summary) return { collapsed: false, reasons: [] };
  const reasons = [];
  const f = summary.freshness || {};
  if (f.is_stale === true) reasons.push("the figures it cites have since changed");
  else if (f.is_stale === null || f.is_stale === undefined) { if (f.reasons && f.reasons.length) reasons.push("its freshness could not be fully verified"); }
  const created = summary.created_at ? Date.parse(summary.created_at) : NaN;
  if (!Number.isNaN(created)) {
    const ageH = (nowMs - created) / 3600000;
    if (rolling && ageH > maxAgeHours) reasons.push("it was written for a rolling window that has since moved on");
    else if (!rolling && ageH > maxAgeHours * 7) reasons.push("it is more than a week old");
  }
  return { collapsed: reasons.length > 0, reasons };
};

export const prettyKey = (k) => String(k).replaceAll("_", " ").replace(/^./, (c) => c.toUpperCase());
